import { Seaport } from "@opensea/seaport-js";
import { CreateOrderInput } from "@opensea/seaport-js/lib/types";
import axios from "axios";
import { ethers } from "ethers";
import { forwardRef, useState } from "react";
import styled from "styled-components";
import CloseCircle from "../../components/Buttons/CloseCircle";
import SimpleButton from "../../components/Buttons/SimpleButton";
import Column from "../../components/Column/Column";
import ErrorInputWithIcon from "../../components/Inputs/ErrorInputWithIcon";
import InputWithLabel from "../../components/InputWithLabel/InputWithLabel";
import { Modal } from "../../components/Modal/Modal";
import {
  CircularSVG,
  CustomCircle
} from "../../components/ProgressBar/CircluarSVG";
import Row from "../../components/Row/Row";
import { BlockChainErrorText } from "../../components/Text/BlockchainErrorText";
import InputErrorText from "../../components/Text/InputErrorText";
import { ProgressText } from "../../components/Text/ProgressText";
import RequiredText from "../../components/Text/RequiredText";
import Text from "../../components/Text/Text";
import { useAuth } from "../../hooks/useAuth";
import close_circle from "../../imgs/close-circle.svg";
import eth_icon from "../../imgs/eth_icon_blue.svg";
import { getSelectedProvider } from "../../utils";
import Song from "../models/Song";

interface ListTokenToMarketplaceModalProps {
  song: Song;
  onListToMarketplaceCloseClick: any;
  onListSuccess: any;
}

const wrappedEthTestNetAddress = "0xA6FA4fB5f76172d178d61B04b0ecd319C5d1C0aa";
const wrappedEthMainnetAddress = "0x7ceB23fD6bC0adD59E62ac25578270cFf1b9f619";

const SongCheckoutContainer = styled("div")`
  display: flex;
  border: 1px solid #ffffff90;
  border-radius: 12px;
  width: 100%;
`;

const SongCheckoutImg = styled("img")`
  width: 200px;
  height: 150px;
  position: relative;
  display: inline-block;
  overflow: hidden;
  margin: 0;
  border-radius: 12px;
`;
const RequiredEthText = styled(RequiredText)`
  &:after {
    content: "* (ETH)";
  }
`;

const DisabledButton = styled(SimpleButton)`
  background: grey;
  margin-left: auto;
  margin-right: auto;
`;

const ModalContainer = styled(Column)`
  padding-inline: 2rem;
  position: relative;
  background: #000d30;
  border-radius: 20px;
  min-width: 30%;
  text-align: left;
  padding-block: 3%;
  border: 1px solid #ffffff90;
`;

const ListTokenToMarketplaceModal = forwardRef<
  HTMLDivElement,
  ListTokenToMarketplaceModalProps
>(({ song, onListToMarketplaceCloseClick, onListSuccess }, ref) => {
  const auth = useAuth();
  const [validInputEth, setValidInputEth] = useState(true);
  const decimalNumberRegex = /^\d*\.?\d+$/;
  const [ethPrice, setEthPrice] = useState("");
  const [placingOrder, setPlacingOrder] = useState(false);
  const [errorText, setErrorText] = useState("");
  const [progressText, setProgressText] = useState("");
  const isMainnet = process.env.REACT_APP_USE_POLYGON_MAINNET === "true";

  const createOrder = async (provider) => {
    const seller = await provider.getSigner().getAddress();
    const seaport = new Seaport(provider);
    const standardCreateOrderInput: CreateOrderInput = {
      offer: [
        {
          itemType: 2,
          token: song.nft_address,
          identifier: song.tokenId.toString(),
        },
      ],
      // Use wrapped ether instead of the native currency on chain

      consideration: [
        {
          amount: ethers.utils.parseEther(ethPrice).toString(),
          token: isMainnet ? wrappedEthMainnetAddress : wrappedEthTestNetAddress,
          recipient: seller,
        },
      ],
      fees: [
        {
          recipient: process.env.REACT_APP_COMPOSENFT_BANK_ADDRESS,
          basisPoints: parseInt(process.env.REACT_APP_NFT_REVENUE_FEE),
        },
        {
          recipient: song.artist_wallet_address,
          basisPoints: song.royalty_percentage * 100,
        },
      ],
    };

    const { executeAllActions } = await seaport.createOrder(
      standardCreateOrderInput,
      seller
    );

    return await executeAllActions();
  };

  const onListToMarketplace = async () => {
    if (window.ethereum) {
      setPlacingOrder(true);
      setProgressText('Please complete your order through wallet extension.')
      const selectedProvider = getSelectedProvider();
      const provider = new ethers.providers.Web3Provider(selectedProvider);

      let order = null;
      try {
        order = await createOrder(provider);
      } catch (error) {
        if (error.toString().includes('user rejected transaction')) {
          setErrorText("Rejected transaction, please try again");
        } else {
          setErrorText("Something went wrong, please try again");
        }
        setPlacingOrder(false);
        return;


      }

      if (order !== null) {
        const jsonData = {
          song_token_id: song.id,
          price: ethPrice,
          user_address: auth.user.wallet_address,
          order: order,
          is_song_token: true,
        };
        axios
          .post(
            process.env.REACT_APP_BACKEND_URL + "/create_listing",
            JSON.stringify(jsonData),
            {
              headers: {
                "Content-Type": "application/json",
              },
            }
          )
          .then((response) => {
            onListSuccess();
          })
          .catch((response) => {
          });
      }
    }
  };

  const onEthPriceChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const num = event.target.value;
    if (!decimalNumberRegex.test(num)) {
      setValidInputEth(false);
      setEthPrice(event.target.value);
      return;
    }

    setValidInputEth(true);
    setEthPrice(event.target.value);
  };

  return (
    <Modal backgroundOpacity={"rgb(0,43,66,0.9)"} ref={ref} show={true}>
      <ModalContainer>
        <CloseCircle
          bg={close_circle}
          onClick={onListToMarketplaceCloseClick}
        />
        <Text bold={true} size={"1.2rem"}>
          List to Marketplace
        </Text>
        <SongCheckoutContainer>
          <SongCheckoutImg src={song.song_cover_s3_link} />
          <Column style={{ justifyContent: "center", paddingLeft: "10%" }}>
            <Text bold={true}>{song.song_name}</Text>
          </Column>
        </SongCheckoutContainer>
        <InputWithLabel>
          <RequiredEthText>Price</RequiredEthText>
          <ErrorInputWithIcon
            valid={validInputEth}
            src={eth_icon}
            onChange={onEthPriceChange}
            placeholder="enter list price here"
          />
        </InputWithLabel>
        {!validInputEth && (
          <InputErrorText>Input must be number!</InputErrorText>
        )}

        <Row align={"flex-start"}>
          <Text>Royalties:</Text>
          <Text style={{ marginLeft: "auto" }}>{song.royalty_percentage}%</Text>
        </Row>

        <Row align={"flex-start"}>
          <Text>Service Fee:</Text>
          <Text style={{ marginLeft: "auto" }}>
            {parseInt(process.env.REACT_APP_NFT_REVENUE_FEE) / 100}%
          </Text>
        </Row>

        <Row align={"flex-start"}>
          <Text>Total:</Text>
          <Text style={{ marginLeft: "auto" }}>{ethPrice} ETH</Text>
        </Row>

        <Row align="flex-start" style={{ marginTop: "1rem" }}>
          {validInputEth && ethPrice !== "" && parseFloat(ethPrice) > 0 ? (
            <>
              {!placingOrder ? (
                <Column style={{marginLeft: 'auto', marginRight: 'auto'}}>
                  <SimpleButton
                    style={{ marginLeft: "auto", marginRight: "auto" }}
                    onClick={onListToMarketplace}
                  >
                    Publish
                  </SimpleButton>
                  <BlockChainErrorText>{errorText}</BlockChainErrorText>
                </Column>
              ) : (
                <Column style={{justifyContent: 'center', alignItems: 'center', marginLeft: 'auto', marginRight: 'auto'}}>
                  <SimpleButton
                    style={{ paddingInline: "15%" }}
                  >
                    <CircularSVG>
                      <CustomCircle
                        cx="15"
                        cy="15"
                        r="10"
                        fill="none"
                        stroke-width="5"
                        stroke-miterlimit="10"
                      />
                    </CircularSVG>
                  </SimpleButton>
                  <ProgressText>{progressText}</ProgressText>
                </Column>
              )}
            </>
          ) : (
            <DisabledButton bg="primary" disabled>
              Publish
            </DisabledButton>
          )}
        </Row>
      </ModalContainer>
    </Modal>
  );
});

export default ListTokenToMarketplaceModal;
